import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { Modal } from 'antd'
import { message } from 'antd'
import api from '../utils/api'

const LoginPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #e8e8e8 0%, #d4c4b0 50%, #c4b5c0 100%);
  padding: 20px;

  @media (max-width: 820px) {
    padding: 20px 16px;
    box-sizing: border-box;
  }
`

const FormContainer = styled.form`
  background: #fef7f7;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(191, 28, 95, 0.15);
  width: 100%;
  max-width: 450px;
  text-align: center;
  border: 1px solid rgba(168, 181, 192, 0.3);

  @media (max-width: 820px) {
    padding: 30px 20px;
    width: 100%;
    max-width: 100%;
    margin: 0;
    box-sizing: border-box;
  }

  h1 {
    font-size: 28px;
    margin-bottom: 10px;
    color: #bf1c5f;
    font-weight: 500;
    letter-spacing: 0.5px;

    @media (max-width: 820px) {
      font-size: 24px;
    }
  }

  p {
    color: #8a8a8a;
    font-size: 16px;
    margin-bottom: 20px;
    font-weight: 300;

    @media (max-width: 820px) {
      font-size: 14px;
      margin-bottom: 15px;
    }
  }

  a {
    color: #bf1c5f;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #a01850;
      text-decoration: underline;
    }
  }
`

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  margin: 12px 0;
  border: 1.5px solid #d4c4b0;
  border-radius: 12px;
  box-sizing: border-box;
  font-size: 16px;
  transition: all 0.3s ease;
  background-color: #ffffff;
  color: #4a4a4a;

  &:focus {
    outline: none;
    border-color: #a8b5c0;
    box-shadow: 0 0 0 3px rgba(168, 181, 192, 0.15);
  }

  &::placeholder {
    color: #b8b8b8;
  }

  @media (max-width: 820px) {
    padding: 12px 14px;
    font-size: 15px;
    margin: 10px 0;
  }
`

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #bf1c5f 0%, #a01850 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 18px;
  font-weight: 500;
  transition: all 0.3s ease;
  margin-top: 10px;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(191, 28, 95, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(191, 28, 95, 0.35);
    background: linear-gradient(135deg, #a01850 0%, #8d1445 100%);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 820px) {
    padding: 12px;
    font-size: 16px;
  }
`

const ErrorMessage = styled.p`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
  font-weight: 400;
`

const Logo = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 20px;

  @media (max-width: 820px) {
    width: 60px;
    height: 60px;
    margin-bottom: 15px;
  }
`

const Footer = styled.p`
  color: #9a9a9a;
  font-size: 14px;
  margin-top: 20px;
  text-align: center;
  font-weight: 300;

  @media (max-width: 820px) {
    font-size: 12px;
    margin-top: 15px;
  }
`

const ContactInfo = styled.p`
  color: #9a9a9a;
  font-size: 14px;
  margin-top: 15px;
  text-align: center;
  font-weight: 300;

  @media (max-width: 820px) {
    font-size: 12px;
  }
`

const Login = () => {
  const [loginData, setLoginData] = useState({ username: '', password: '' })
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { login, user, businesses } = useAuth()

  useEffect(() => {
    if (user) {
      if (Array.isArray(businesses) && businesses.length > 1) {
        navigate('/select-business')
      } else {
        navigate('/appointments')
      }
    }
  }, [user, businesses, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    try {
      const result = await login(loginData)
      if (result) {
        if (Array.isArray(result.businesses) && result.businesses.length > 1) {
          navigate('/select-business')
        } else {
          navigate('/appointments')
        }
      } else {
        setError('登入失敗，請重新嘗試。')
      }
    } catch (error) {
      if (
        error.response?.status === 403 &&
        error.response.data?.subscriptionExpired
      ) {
        Modal.error({
          title: '會員方案已到期',
          content: (
            <div>
              <p>您的會員方案已到期，請聯繫客服處理續約事宜。</p>
              <p style={{ marginTop: '12px' }}>客服 LINE：@488pasak</p>
              <p style={{ color: '#888', fontSize: '14px', marginTop: '8px' }}>
                續約後即可繼續使用所有功能
              </p>
            </div>
          ),
          okText: '我知道了'
        })
        return
      }
      setError('登入失敗，請檢查帳號密碼是否正確')
      message.error('登入失敗，請檢查帳號密碼是否正確')
    }
  }

  return (
    <LoginPageContainer>
      <FormContainer onSubmit={handleSubmit}>
        <Logo src={'/saa.png'} alt='公司logo' />
        <h1>BeauteFeel 後台管理系統</h1>
        <p>輕鬆管理你的客戶，提升工作效率。</p>
        <Input
          type='text'
          value={loginData.username}
          onChange={(e) =>
            setLoginData({ ...loginData, username: e.target.value })
          }
          placeholder='帳號'
          required
        />
        <Input
          type='password'
          value={loginData.password}
          onChange={(e) =>
            setLoginData({ ...loginData, password: e.target.value })
          }
          placeholder='密碼'
          required
        />
        <Button type='submit'>登入</Button>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Footer>美感 | BeauteFeel</Footer>
        <ContactInfo>
          商務合作請洽{' '}
          <a href='mailto:service@beautefeel.com'>美感 | BeauteFeel</a>
        </ContactInfo>
      </FormContainer>
    </LoginPageContainer>
  )
}

export default Login
